import { HttpClient } from '@/components/shared/http-client'
import { prettyPolicyType } from '@/components/applications/policy-types'
import { format } from 'date-fns'
import * as documentUtils from '@/components/documents/documents-util'

function isDigital(app) {
  return !!app && !!app.globalApp && app.globalApp.type === 'DIGITAL'
}
function isGlobalPdf(app) {
  const isGlobalPdf = !!app && !!app.globalApp && app.globalApp.type === 'PDF'
  return isGlobalPdf
}
function getGlobalAppId(app) {
  if (app && app.globalApp && app.globalApp.globalAppId) {
    return app.globalApp.globalAppId
  }
  return undefined
}
function getCurrentAppId(app) {
  return app.id
}

// Given the Current Application, get the name
function getApplicationName(application) {
  if (application == null) return undefined
  if (application.brokerAppIdToComplete) {
    return application.globalApp.name
  }
  return getDocumentPolicyType(application.application)
}

function getDocumentPolicyType(documentObj) {
  const policyTypeEnum = documentObj?.labelMap?.POLICY_TYPE?.value || ''
  return prettyPolicyType(policyTypeEnum)
}

// Given the Current Application, get the title
function getApplicationTitle(application) {
  if (application == null) return undefined
  if (application.brokerAppIdToComplete) {
    return application.globalApp.title
  }
  return application.application.name
}

// Given the Current Application, get the year
function getApplicationYear(application) {
  if (application.brokerAppIdToComplete) {
    const date = format(new Date(), 'yyyy')
    return date
  }
  return getDocumentYear(application.application)
}
function getDocumentYear(documentObj) {
  return documentObj.labelMap?.YEAR?.value || ''
}

// Given the Current Application, get the provider
function getApplicationProvider(application) {
  if (application.brokerAppIdToComplete) {
    return application.globalApp.provider
  }
  return getDocumentProvider(application.application)
}

function getDocumentProvider(documentObj) {
  return documentObj.labelMap?.CARRIER?.value || ''
}

function getProviderAndName(application) {
  const name = getApplicationName(application)
  const provider = getApplicationProvider(application)
  const providerAndName = `${provider} - ${name}`
  return providerAndName
}

function isPdfApplication(application) {
  if (application == null) {
    return false
  }
  if (application && application.brokerAppIdToComplete && application.globalApp.type === 'DIGITAL') {
    return false
  }
  return true
}

function getPdfDocumentIdentifier(application) {
  if (isGlobalPdf(application)) {
    return application.globalApp.identifier
  } else {
    const identifier = application.application?.identifier
    return identifier
  }
}

async function downloadReferenceApplicationFromServer(accountCode, referenceApplicationIdentifier) {
  return documentUtils.downloadDocument(accountCode, referenceApplicationIdentifier)
}

async function downloadApplicationFromServer(accountCode, applicationIdentifier) {
  const url = `/api/accounts/${accountCode}/apps/file/${applicationIdentifier}`
  // { data, headers }
  const response = await HttpClient.get(url, { responseType: 'blob' })
  return response
}

async function downloadDigitalApplicationFromServer(accountCode, digitalIdentifier) {
  const url = `/api/accounts/${accountCode}/apps/digital/${digitalIdentifier}`
  // { data, headers }
  const response = await HttpClient.get(url, { responseType: 'blob' })
  return response
}

async function downloadDigitalUserApplicationFromServer(token, digitalIdentifier) {
  const url = `/open/apps/${token}/download-digital/${digitalIdentifier}`
  // { data, headers }
  const response = await HttpClient.get(url, { responseType: 'blob' })
  return response
}

async function getApplications(accountCode) {
  const url = `/api/accounts/${accountCode}/apps`
  const { data } = await HttpClient.get(url)
  return data
}

function markIncompleteUrl(accountCode, token, currentAppId) {
  if (token) {
    return `/open/apps/${token}/current-apps/${currentAppId}/digital-undone`
  }
  return `/api/accounts/${accountCode}/apps/${currentAppId}/digital-undone`
}

async function markDigitalAppIncomplete(accountCode, token, currentAppId) {
  const url = markIncompleteUrl(accountCode, token, currentAppId)
  const resp = await HttpClient.put(url)
  return resp
}

function getApplicationExtension(application) {
  if (!application) {
    return undefined
  }
  if (application.brokerAppIdToComplete) {
    if (application.globalApp.type === 'DIGITAL') {
      return undefined
    }
    if (application.globalApp.type === 'PDF') {
      return 'pdf'
    }
  }
  const extension = application.application.extension
  return extension
}
// { count: #,
// documents: [], // {}
// }
function getReferences(application) {
  const references = {
    count: 0,
    documents: [],
  }
  if (application == null || application.references == null) {
    return references
  }
  references.count = application.references.length
  references.documents = application.references
  return references
}

function processPdfToComplete(app, root) {
  const identifier = getPdfDocumentIdentifier(app)
  const name = getApplicationName(app)
  const extension = getApplicationExtension(app)
  const isGlobal = isGlobalPdf(app)
  const currentAppId = getCurrentAppId(app)
  const doc = {
    identifier: identifier,
    currentAppId: currentAppId,
    name: name,
    extension: extension,
    isGlobalPdf: isGlobal,
  }
  root.$emit('mark-app-as-complete', doc)
}

function downloadAppFromServerToBrowser(data, headers) {
  const { 'content-disposition': contentDisposition = '' } = headers || {}
  const fileName = contentDisposition.replace(/^attachment; filename=/i, '')
  downloadFileToClient(new Blob([data]), fileName)
}
function downloadFileToClient(file, filename) {
  const link = document.createElement('a')
  link.setAttribute('download', filename)
  link.href = window.URL.createObjectURL(file)
  document.body.appendChild(link)
  link.click()
  link.remove()
}

async function markDigitalUserAppDoneToken(token, frontEndIdentifier) {
  const url = `/open/apps/${token}/current-apps/front-end/${frontEndIdentifier}/digital-done`
  return await HttpClient.post(url)
}

async function markDigitalAccountAppDone(accountCode, frontEndIdentifier) {
  const url = `/api/accounts/${accountCode}/apps/front-end/${frontEndIdentifier}/digital-done`
  return await HttpClient.post(url)
}

export {
  downloadAppFromServerToBrowser,
  processPdfToComplete,
  getApplications,
  downloadApplicationFromServer,
  downloadDigitalApplicationFromServer,
  downloadDigitalUserApplicationFromServer,
  downloadReferenceApplicationFromServer,
  isDigital,
  isGlobalPdf,
  getGlobalAppId,
  getCurrentAppId,
  getApplicationName,
  getApplicationTitle,
  getApplicationYear,
  getApplicationProvider,
  getApplicationExtension,
  isPdfApplication,
  getProviderAndName,
  getReferences,
  getDocumentYear,
  getDocumentPolicyType,
  getDocumentProvider,
  getPdfDocumentIdentifier,
  markDigitalAppIncomplete,
  markDigitalUserAppDoneToken,
  markDigitalAccountAppDone,
}
